
import { useState, useEffect } from 'react';
import { useCompanyStore } from '@/lib/stores/companyStore';
import { supabase } from '@/integrations/supabase/client';
import type { Company } from '@/lib/types';

export const useCompanySearch = (initialTerm: string = '') => {
  const { companies } = useCompanyStore();
  const [searchTerm, setSearchTerm] = useState(initialTerm);
  const [hasSearched, setHasSearched] = useState(false);
  const [supabaseCompanies, setSupabaseCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Fetch companies from Supabase when the component mounts
  useEffect(() => {
    const fetchSupabaseCompanies = async () => {
      try {
        setIsLoading(true);
        
        // Get companies from the "reviews" table by extracting unique company names
        const { data: reviewsData, error: reviewsError } = await supabase
          .from('reviews')
          .select('company, location')
          .order('created_at', { ascending: false });
          
        if (reviewsError) {
          console.error("Error fetching reviews from Supabase:", reviewsError);
          return;
        }
        
        // Extract unique companies from reviews
        const uniqueCompanies = new Map();
        reviewsData?.forEach(review => {
          if (!uniqueCompanies.has(review.company)) {
            uniqueCompanies.set(review.company, {
              id: crypto.randomUUID(), // Generate an ID
              name: review.company,
              locations: [review.location].filter(Boolean), // Filter out null/undefined locations
              createdAt: Date.now(),
              isVisible: true // Default to visible for companies from reviews
            });
          } else {
            // Add location if not already in the locations array and it's not null/undefined
            const company = uniqueCompanies.get(review.company);
            if (review.location && !company.locations.includes(review.location)) {
              company.locations.push(review.location);
            }
          }
        });
        
        // Also fetch from the companies table to ensure we get ALL companies
        const { data: companiesData, error: companiesError } = await supabase
          .from('companies')
          .select('*')
          .order('name', { ascending: true });
          
        if (companiesError) {
          console.error("Error fetching companies from Supabase:", companiesError);
        } else if (companiesData) {
          // Add companies from the companies table to our map
          companiesData.forEach(company => {
            if (!uniqueCompanies.has(company.name)) {
              uniqueCompanies.set(company.name, {
                id: company.id,
                name: company.name,
                locations: company.locations || [],
                createdAt: new Date(company.created_at).getTime(),
                bio: company.bio || '',
                staffSize: company.staff_size || '',
                logo: company.logo || '',
                website: company.website || '',
                twitterUrl: company.twitter_url || '',
                instagramUrl: company.instagram_url || '',
                linkedinUrl: company.linkedin_url || '',
                isVisible: company.is_visible
              });
            } else {
              // Update existing company with data from companies table
              const existingCompany = uniqueCompanies.get(company.name);
              existingCompany.id = company.id;
              existingCompany.bio = company.bio || '';
              existingCompany.staffSize = company.staff_size || '';
              existingCompany.logo = company.logo || '';
              existingCompany.website = company.website || '';
              existingCompany.twitterUrl = company.twitter_url || '';
              existingCompany.instagramUrl = company.instagram_url || '';
              existingCompany.linkedinUrl = company.linkedin_url || '';
            }
          });
        }
        
        setSupabaseCompanies(Array.from(uniqueCompanies.values()));
      } catch (error) {
        console.error("Error fetching companies:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSupabaseCompanies();
  }, []);
  
  // Filter visible companies from the local store
  const visibleLocalCompanies = companies.filter(company => company.isVisible !== false);
  
  // Combine local and Supabase companies, removing duplicates
  const allCompanies = [...visibleLocalCompanies];
  
  // Add Supabase companies that don't exist in local storage
  supabaseCompanies.forEach(supabaseCompany => {
    const existsInLocal = visibleLocalCompanies.some(
      localCompany => localCompany.name.toLowerCase() === supabaseCompany.name.toLowerCase()
    );
    
    if (!existsInLocal) {
      allCompanies.push(supabaseCompany);
    }
  });
  
  // Find matching companies based on company name
  const matchingCompanies = allCompanies.filter(company => 
    company.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Create company cards for display
  const companyCards = matchingCompanies.map(company => ({
    id: company.id,
    company: company.name,
    jobTitle: '',
    reasonForExit: '',
    location: company.locations && company.locations.length > 0 ? company.locations[0] : '',
    createdAt: company.createdAt,
    isCompanyOnly: true,
    logo: company.logo || '',
    website: company.website || '',
    staffSize: company.staffSize || ''
  }));

  const handleSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setHasSearched(true);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setHasSearched(false);
  };

  return {
    searchTerm,
    setSearchTerm,
    hasSearched,
    setHasSearched,
    companyCards,
    handleSearch,
    clearSearch,
    isLoading
  };
};
